import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Link } from "react-router";
import {
	Button as CoreButton,
	Link as CoreLink
} from "@sparefoot/react-commons";

import "./Button.scss";
import useClickTracking from "hooks/tracking/useClickTracking";

function Button({
	theme = "basic",
	fullWidth = true,
	reverse,
	children,
	className,
	disabled,
	type = "button",
	title = null,
	label,
	href = null,
	to = null,
	getRef,
	onClick,
	onMouseEnter,
	onMouseLeave,
	withIcon,
	...rest
}) {
	const { handleClick } = useClickTracking({ onClick, href });

	const buttonProps = {
		"aria-label": label || title,
		type,
		disabled,
		title: label || title
	};

	const linkProps = {
		href
	};

	// Pass through any provided aria-* attributes without adding new public props
	const ariaProps = Object.keys(rest)
		.filter((key) => key.startsWith("aria-"))
		.reduce((acc, key) => {
			acc[key] = rest[key];
			return acc;
		}, {});

	let sharedProps = {
		...rest,
		onClick: handleClick,
		onMouseEnter,
		onMouseLeave,
		ref: getRef,
		className: classnames(
			className,
			"sf-button",
			{
				disabled,
				reverse,
				fullWidth,
				withIcon
			},
			!!theme && `theme-${theme}`
		)
	};

	if (to) {
		sharedProps = { ...sharedProps, ...ariaProps, to };
	} else if (href) {
		sharedProps = { ...sharedProps, ...linkProps, ...ariaProps };
	} else {
		sharedProps = { ...sharedProps, ...buttonProps, ...ariaProps };
	}

	let Element = CoreButton;
	if (to) Element = Link;
	else if (href) Element = CoreLink;

	return React.createElement(Element, { ...sharedProps }, children);
}

Button.propTypes = {
	fullWidth: PropTypes.bool,
	reverse: PropTypes.bool,
	theme: PropTypes.oneOf([
		"primary",
		"secondary",
		"tertiary",
		"text",
		"icon",
		"basic"
	]),
	children: PropTypes.node,
	className: PropTypes.string,
	href: PropTypes.string,
	to: PropTypes.string,
	type: PropTypes.string,
	getRef: PropTypes.func,
	disabled: PropTypes.bool,
	onClick: PropTypes.func,
	onMouseEnter: PropTypes.func,
	onMouseLeave: PropTypes.func,
	withIcon: PropTypes.bool,
	title: PropTypes.string,
	label: PropTypes.string
};

export default Button;
